// Generic JSON ↔ URL params helpers (schema-free)
import { isArray, isDate, isNil, isPlainObject, set, toString, update } from 'lodash';

// Build bracket notation key
const makeKey = (parent, key) => (parent ? `${parent}[${key}]` : key);

// Recursively append values to URLSearchParams
const appendParam = (params, key, value) => {
    if (isNil(value)) {
        if (value === undefined) return; // skip undefined
        params.append(key, '');
        return;
    }

    if (isDate(value)) {
        params.append(key, value.toISOString());
        return;
    }

    if (isArray(value)) {
        // Scalars: repeated keys; Objects: use indexed bracket notation
        const allScalars = value.every((v) => !isPlainObject(v) && !isArray(v));
        if (allScalars) {
            for (const item of value) {
                if (isNil(item)) {
                    if (item === undefined) continue;
                    params.append(key, '');
                    continue;
                }
                if (isDate(item)) {
                    params.append(key, item.toISOString());
                    continue;
                }
                params.append(key, toString(item));
            }
        } else {
            for (let i = 0; i < value.length; i++) {
                appendParam(params, `${key}[${i}]`, value[i]);
            }
        }
        return;
    }

    if (isPlainObject(value)) {
        for (const [k, v] of Object.entries(value)) {
            appendParam(params, makeKey(key, k), v);
        }
        return;
    }

    params.append(key, toString(value));
};

/**
 * Convert a JSON object to a URLSearchParams object
 * @param {Object} json - The JSON object to convert to a URLSearchParams object
 * @returns {URLSearchParams} - The URLSearchParams object
 */
function toSearchParams(json) {
    const params = new URLSearchParams();
    if (!json || typeof json !== 'object') return params;
    for (const [k, v] of Object.entries(json)) {
        appendParam(params, k, v);
    }
    return params;
}

/**
 * Return a search string beginning with '?' or '' if empty
 * @param {Object} json - The JSON object to convert to a search string
 * @returns {string} - The search string
 */
function toSearchString(json) {
    const p = toSearchParams(json);
    const s = p.toString();
    return s ? `?${s}` : '';
}

/**
 * Convert URLSearchParams/string back into a nested JSON object
 * @param {string | URLSearchParams} searchOrParams - The URLSearchParams or string to convert
 * @returns {Object} - The nested JSON object
 */
function fromSearchParams(searchOrParams) {
    const params =
        typeof searchOrParams === 'string'
            ? new URLSearchParams(searchOrParams.startsWith('?') ? searchOrParams.slice(1) : searchOrParams)
            : searchOrParams;

    const result = {};

    params.forEach((value, key) => {
        // Plain keys: accumulate repeats into arrays
        if (!key.includes('[')) {
            if (result[key] === undefined) result[key] = value;
            else if (isArray(result[key])) result[key].push(value);
            else result[key] = [result[key], value];
            return;
        }

        // Bracketed keys
        if (key.includes('[]')) {
            // Push semantics for any [] segments
            const basePath = key.replace(/\[\]/g, '');
            const lodashPath = basePath.replace(/\[(\d+)\]/g, '.$1').replace(/\[([^\]]+)\]/g, '.$1');
            update(result, lodashPath, (arr) => (isArray(arr) ? [...arr, value] : [value]));
            return;
        }

        // Simple bracket paths (no push): use lodash.set
        const lodashPath = key.replace(/\[(\d+)\]/g, '.$1').replace(/\[([^\]]+)\]/g, '.$1');
        set(result, lodashPath, value);
    });

    return result;
}

/**
 * Push or replace browser URL with params from JSON
 * @param {Object} json - The JSON object to push to the browser URL
 * @param {Object} options - The options for the push to browser URL
 * @param {boolean} options.replace - Whether to replace the current URL or push a new one
 * @returns {void}
 */
function pushToBrowserUrl(json, options = {}) {
    const { replace = false } = options;
    const qs = toSearchString(json);
    const newUrl = `${window.location.pathname}${qs}`;
    if (replace) {
        window.history.replaceState({ path: newUrl }, '', newUrl);
    } else {
        window.history.pushState({ path: newUrl }, '', newUrl);
    }
}

export default {
    toSearchParams,
    toSearchString,
    fromSearchParams,
    pushToBrowserUrl,
};
